/**
 * External dependencies
 */
import s from 'store';
import type { Maybe } from '@nab/types';

const store = s as Store;
type Store = {
	readonly set: < T >( key: string, value: StoreValue< T > ) => void;
	readonly get: < T >( key: string ) => Maybe< StoreValue< T > >;
	readonly remove: ( key: string ) => void;
	readonly each: ( fn: ( v: StoreValue, k: string ) => void ) => void;
};

type StoreValue< T = unknown > = {
	readonly value: T;
	readonly expiration?: number;
};

/**
 * Saves the given key/value pair in the browser’s store.
 *
 * @param {string} key      a key that identifies the value.
 * @param {any}    value    the specific value.
 * @param {number} lifespan Optional. The number of seconds the value should be valid.
 *                          If not set, the key/value pair never expires.
 */
export function setValue( key: string, value: unknown, lifespan = 0 ): void {
	const storeKey = getStoreKey( key );
	if ( lifespan ) {
		store.set( storeKey, {
			value,
			expiration: new Date().getTime() + lifespan * 1000,
		} );
	} else {
		store.set( storeKey, { value } );
	}
}

/**
 * Returns the value of the given key if any. If it’s not set, it returns `default`.
 *
 * @param {string} key          the key that identifies the value we’re interested in.
 * @param {any}    defaultValue Optional. Default value.
 *
 * @return {any} the value of the given key in the store, if available and valid; `default`, if there’s no such value; `undefined` otherwise.
 */
export function getValue< T >( key: string, defaultValue: T ): T;
export function getValue< T >( key: string, defaultValue?: T ): Maybe< T >;
export function getValue< T >( key: string, defaultValue?: T ): Maybe< T > {
	const storeKey = getStoreKey( key );
	const value = store.get< T >( storeKey );
	if ( ! value ) {
		return defaultValue;
	}

	if ( value.expiration && value.expiration < new Date().getTime() ) {
		clearValue( key );
		return defaultValue;
	}

	return value.value;
}

export function clearValue( key: string ): void {
	const storeKey = getStoreKey( key );
	store.remove( storeKey );
}

function getStoreKey( key: string ): string {
	return `NelioABTesting[${ key }]`;
}

store.each( ( value: StoreValue, key: string ) => {
	if ( ! key.startsWith( 'NelioABTesting' ) ) {
		return;
	}

	if ( ! value || ! value.expiration ) {
		return;
	}

	if ( value.expiration > new Date().getTime() ) {
		return;
	}

	store.remove( key );
} );
